import jsPDF from "jspdf";
import type { PipeSegment, Fitting, CutListItem, FittingType } from "@/hooks/use-sketch-state";
import { gridToIso, pipeStrokeWidth } from "@/hooks/use-sketch-state";

const materialPdfColors: Record<string, [number, number, number]> = {
  Copper: [200, 110, 50],
  Steel: [120, 125, 140],
  PVC: [170, 175, 180],
  CPVC: [190, 160, 60],
  Stainless: [150, 155, 165],
  "Cast Iron": [80, 85, 95],
};

const fittingLabels: Record<FittingType, string> = {
  elbow90: "90° Ell",
  elbow45: "45° Ell",
  tee: "Tee",
  reducer: "Reducer",
  coupling: "Coupling",
  valve: "Valve",
  cap: "Cap",
};

const MARGIN = 15;
const PAGE_W = 279.4; // 11" landscape
const PAGE_H = 215.9; // 8.5"
const CONTENT_W = PAGE_W - MARGIN * 2;

function getColor(material: string): [number, number, number] {
  return materialPdfColors[material] || [80, 80, 80];
}

function computeBounds(segments: PipeSegment[], fittings: Fitting[]) {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const seg of segments) {
    for (const pt of [seg.start, seg.end]) {
      const p = gridToIso(pt.gx, pt.gy);
      minX = Math.min(minX, p.x); minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x); maxY = Math.max(maxY, p.y);
    }
  }
  for (const fit of fittings) {
    const p = gridToIso(fit.position.gx, fit.position.gy);
    minX = Math.min(minX, p.x); minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x); maxY = Math.max(maxY, p.y);
  }
  if (!isFinite(minX)) return { minX: 0, minY: 0, maxX: 100, maxY: 100 };
  // Add padding
  const pad = 30;
  return { minX: minX - pad, minY: minY - pad, maxX: maxX + pad, maxY: maxY + pad };
}

export function generateSketchPDF(
  segments: PipeSegment[],
  fittings: Fitting[],
  cutList: CutListItem[],
  totalFootage: number,
  sketchName?: string,
) {
  const doc = new jsPDF({ unit: "mm", format: "letter", orientation: "landscape" });

  // ===== PAGE 1: Isometric Drawing =====
  const bounds = computeBounds(segments, fittings);
  const bw = bounds.maxX - bounds.minX || 1;
  const bh = bounds.maxY - bounds.minY || 1;

  // Drawing area on page 1
  const drawAreaW = CONTENT_W;
  const drawAreaH = PAGE_H - MARGIN * 2 - 20; // leave room for title
  const scale = Math.min(drawAreaW / (bw * 0.264), drawAreaH / (bh * 0.264)) * 0.9; // px to mm approx
  const pxToMm = 0.264 * scale;

  const offsetX = MARGIN + drawAreaW / 2 - ((bounds.minX + bounds.maxX) / 2) * pxToMm;
  const offsetY = MARGIN + 18 + drawAreaH / 2 - ((bounds.minY + bounds.maxY) / 2) * pxToMm;

  const toPage = (px: number, py: number) => ({
    x: offsetX + px * pxToMm,
    y: offsetY + py * pxToMm,
  });

  // Title
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor(40, 40, 40);
  doc.text(sketchName || "Pipe Sketch", MARGIN, MARGIN + 6);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(120, 120, 120);
  doc.text(
    `${segments.length} pipe runs · ${fittings.length} fittings · ${totalFootage.toFixed(1)} ft total · ${new Date().toLocaleDateString()}`,
    MARGIN, MARGIN + 12
  );

  // Border around drawing area
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.3);
  doc.rect(MARGIN, MARGIN + 16, drawAreaW, drawAreaH);

  // Draw pipe segments
  for (const seg of segments) {
    const s = gridToIso(seg.start.gx, seg.start.gy);
    const e = gridToIso(seg.end.gx, seg.end.gy);
    const ps = toPage(s.x, s.y);
    const pe = toPage(e.x, e.y);
    const color = getColor(seg.material);
    const sw = pipeStrokeWidth(seg.size) * pxToMm * 0.8;

    // Shadow
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(sw + 0.3);
    doc.line(ps.x, ps.y + 0.3, pe.x, pe.y + 0.3);
    // Restore with low opacity via lighter color
    doc.setDrawColor(230, 230, 230);
    doc.setLineWidth(sw + 0.3);
    doc.line(ps.x, ps.y + 0.3, pe.x, pe.y + 0.3);

    // Main pipe
    doc.setDrawColor(color[0], color[1], color[2]);
    doc.setLineWidth(sw);
    doc.line(ps.x, ps.y, pe.x, pe.y);

    // Length label
    const mx = (ps.x + pe.x) / 2;
    const my = (ps.y + pe.y) / 2 - sw - 1.5;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6);
    doc.setTextColor(60, 60, 60);
    doc.text(`${seg.lengthFt.toFixed(1)}'`, mx, my, { align: "center" });
  }

  // Draw fittings
  for (const fit of fittings) {
    const pos = gridToIso(fit.position.gx, fit.position.gy);
    const p = toPage(pos.x, pos.y);
    const color = getColor(fit.material);

    if (fit.auto && fit.type === "elbow90" && fit.inAngle !== undefined && fit.outAngle !== undefined) {
      // Draw elbow arc approximation as a small angled line pair
      const r = 3;
      const x1 = p.x + r * Math.cos(fit.inAngle);
      const y1 = p.y + r * Math.sin(fit.inAngle);
      const x2 = p.x + r * Math.cos(fit.outAngle);
      const y2 = p.y + r * Math.sin(fit.outAngle);
      doc.setDrawColor(color[0], color[1], color[2]);
      doc.setLineWidth(0.5);
      doc.line(x1, y1, p.x, p.y);
      doc.line(p.x, p.y, x2, y2);
      doc.setFillColor(color[0], color[1], color[2]);
      doc.circle(p.x, p.y, 0.8, "F");
    } else if (fit.auto && fit.type === "tee") {
      doc.setFillColor(color[0], color[1], color[2]);
      doc.circle(p.x, p.y, 1.2, "F");
      // Draw arms
      const arms = [fit.inAngle, fit.outAngle, fit.branchAngle].filter(a => a !== undefined) as number[];
      doc.setDrawColor(color[0], color[1], color[2]);
      doc.setLineWidth(0.5);
      for (const angle of arms) {
        doc.line(p.x, p.y, p.x + 3 * Math.cos(angle), p.y + 3 * Math.sin(angle));
      }
    } else {
      // Manual fitting - circle with label
      doc.setDrawColor(color[0], color[1], color[2]);
      doc.setFillColor(255, 255, 255);
      doc.circle(p.x, p.y, 2.5, "FD");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(4);
      doc.setTextColor(color[0], color[1], color[2]);
      const label = fittingLabels[fit.type] || fit.type;
      doc.text(label.slice(0, 3), p.x, p.y + 0.8, { align: "center" });
    }
  }

  // ===== PAGE 2: Cut List =====
  if (cutList.length > 0) {
    doc.addPage();
    let y = MARGIN;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(40, 40, 40);
    doc.text("Cut List", MARGIN, y + 6);
    y += 10;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(120, 120, 120);
    doc.text(
      `${sketchName || "Pipe Sketch"} · ${totalFootage.toFixed(1)} ft total · ${new Date().toLocaleDateString()}`,
      MARGIN, y
    );
    y += 8;

    // Table header
    doc.setFillColor(240, 240, 240);
    doc.rect(MARGIN, y - 1, CONTENT_W, 7, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(60, 60, 60);
    const cols = [MARGIN + 2, MARGIN + 30, MARGIN + 65, MARGIN + 100, MARGIN + 160];
    doc.text("Size", cols[0], y + 4);
    doc.text("Material", cols[1], y + 4);
    doc.text("Runs", cols[2], y + 4);
    doc.text("Total Footage", cols[3], y + 4);
    doc.text("Fittings", cols[4], y + 4);
    y += 9;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);

    for (const item of cutList) {
      if (y > PAGE_H - 20) {
        doc.addPage();
        y = MARGIN;
      }

      // Alternating row bg
      const rowIdx = cutList.indexOf(item);
      if (rowIdx % 2 === 0) {
        doc.setFillColor(250, 250, 250);
        doc.rect(MARGIN, y - 2, CONTENT_W, 6, "F");
      }

      doc.setTextColor(40, 40, 40);
      doc.text(item.size, cols[0], y + 2);
      doc.text(item.material, cols[1], y + 2);
      doc.text(`${item.segments.length}`, cols[2], y + 2);
      doc.setFont("helvetica", "bold");
      doc.text(`${item.totalFt.toFixed(1)} ft`, cols[3], y + 2);
      doc.setFont("helvetica", "normal");

      const fittingStr = item.fittings.map(f => `${fittingLabels[f.type]} x${f.count}`).join(", ");
      doc.text(fittingStr || "—", cols[4], y + 2);

      y += 7;

      // Individual runs
      for (const seg of item.segments) {
        if (y > PAGE_H - 15) {
          doc.addPage();
          y = MARGIN;
        }
        doc.setTextColor(120, 120, 120);
        doc.setFontSize(7);
        doc.text(`  Run: ${seg.lengthFt.toFixed(1)} ft`, cols[2], y + 1);
        y += 4;
      }
      doc.setFontSize(8);
      y += 2;
    }

    // Grand total
    y += 3;
    doc.setDrawColor(180, 180, 180);
    doc.setLineWidth(0.4);
    doc.line(MARGIN, y, MARGIN + CONTENT_W, y);
    y += 5;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(40, 40, 40);
    doc.text(`Grand Total: ${totalFootage.toFixed(1)} ft`, MARGIN + 2, y);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text(
      `${segments.length} pipe runs · ${fittings.length} fittings`,
      MARGIN + 80, y
    );
  }

  // Footer on every page
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(160, 160, 160);
    doc.text("Trades Toolbelt — Pipe Sketch", MARGIN, PAGE_H - 5);
    doc.text(`Page ${i} of ${pageCount}`, PAGE_W - MARGIN, PAGE_H - 5, { align: "right" });
  }

  const filename = `pipe-sketch-${(sketchName || "export").replace(/\s+/g, "-").toLowerCase()}-${new Date().toISOString().slice(0, 10)}.pdf`;
  doc.save(filename);
}
